/**
 * Route preload registry.
 *
 * Sidebar (and any other nav surface) calls `preloadRoute(item.to)` on
 * hover or focus. That fires the dynamic import for the page's chunk
 * silently; by the time the user actually clicks, the JS is already in
 * the browser cache and React.lazy resolves synchronously — no Suspense
 * fallback, no visible spinner between click and paint.
 *
 * The map is kept next to the routes file (same folder) because it
 * mirrors the same lazy imports one-to-one. Whenever you add a new
 * React.lazy() there, add a matching entry here — otherwise the sidebar
 * silently no-ops on hover for that route.
 *
 * Only lists the biggest pages; short pages don't benefit enough to be
 * worth the memory of loading them just because a mouse crossed the
 * sidebar.
 */

/** Loader shape — same as what React.lazy accepts. */
import { IS_ONPREM } from "@shared/config/edition";

type Loader = () => Promise<unknown>;

/**
 * Path → dynamic import. Keep in sync with app/routes/index.tsx.
 * Only include routes whose page chunk is >~50 KB — small pages don't
 * benefit enough to justify pre-fetching them off a mouse hover.
 */
const PRELOADERS: Record<string, Loader> = {
  "/system-monitoring": () => import("@features/system-monitoring/SystemMonitoringPage"),
  "/auth-access":       () => import("@features/auth-access/AuthAccessPage"),
  "/threat-hunting":    () => import("@features/threat-hunting/ThreatHuntingPage"),
  "/fim-monitoring":    () => import("@features/fim/FimMonitoringPage"),
  "/fim-management":    () => import("@features/fim/FimManagementPage"),
  "/agent-enrollment":  () => import("@features/agents/AgentEnrollmentPage"),
  "/user-management":   () => import("@features/users/UserManagementPage"),
  "/endpoint-inventory": () => import("@/features/endpoint-inventory/EndpointInventoryPage"),
  "/cis-benchmark":     () => import("@features/cis-benchmark/CisBenchmarkPage"),
  "/vulnerability-detection":   () => import("@/features/vulnerability-detection/VulnerabilityDetectionPage"),
  "/compliance":        () => import("@features/compliance/CompliancePage"),
  // Pages that do not exist in the on-prem edition: left out of that bundle.
  ...(IS_ONPREM ? {} : {
    "/organizations":   () => import("@features/organizations/OrganizationsPage"),
    "/mitre-attack":    () => import("@features/mitre-attack/MitreAttackPage"),
  }),
};

/**
 * Track routes we've already asked to preload so hovering the same link
 * ten times doesn't fire ten fetches. Browser cache would dedupe them
 * anyway, but this saves the promise churn.
 */
const started = new Set<string>();

/**
 * Kick off the preload for a route path. Safe to call on every hover
 * event — subsequent calls no-op. Errors are swallowed on purpose:
 * a failed preload just means the user pays the load cost on click.
 */
export function preloadRoute(path: string): void {
  if (started.has(path)) return;
  const loader = PRELOADERS[path];
  if (!loader) return;
  started.add(path);
  // Fire and forget. Any real failure will surface when React.lazy
  // tries the same import at click-time.
  void loader().catch(() => {
    started.delete(path); // let a retry happen on next hover
  });
}
